<template>
  <nav
    v-if="shouldShowSecondaryBreadcrumbs"
    class="breadcrumbs search-breadcrumbs"
    data-test="secondary-breadcrumbs"
  >
    <search-breadcrumbs-generator
      v-if="breadcrumbsInternal"
      :breadcrumbs="breadcrumbsInternal"
      :with-counts="false"
      :allow-last-link="true"
      @update-rendered-url="({ label, url }) => updateRenderedUrl(label, url)"
    >
      <pu-link
        v-bind="homeBreadcrumb"
        exclude-type="fullMinusType"
        :non-seo="true"
      >
        <translate>Home</translate>
      </pu-link>
      <span class="crumb">&rsaquo;</span>
    </search-breadcrumbs-generator>
    <nuxt-to-meta v-if="showSchema" :nuxt2meta="head" />
  </nav>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { Breadcrumb } from '~/apps/search/schemas'
import type { BreadcrumbCounts } from '~/composables/useBreadcrumbCounts'
import { useBreadcrumbCounts } from '~/composables/useBreadcrumbCounts'
import type { SearchFilters } from '~/types/search'
import { getFacetRules } from '~/utility/facets'
import { CATEGORIES } from '../../apps/pu-links/simpleCategories'
import { HomepageLinkGenerator } from '../../apps/url/generators/HomepageLinkGenerator'
import { isGlamping } from '../../utility/glamping'
import { computeSecondaryBreadcrumbs } from '~/apps/breadcrumbs/computeBreadcrumbs'

const props = defineProps<{
  breadcrumbs: Breadcrumb[]
  showSchema?: boolean
  counts?: Record<string, number>
}>()

const searchStore = useSearchStoreSetup(usePinia())
const route = useRoute()
const langCode = (route.params.lang as string) || 'en-gb'

const isGlampingCategory = computed(() => {
  return isGlamping(searchStore.searchFilters.categoryIds || [])
})

const isSeo = computed(() => {
  const searchFilters = searchStore.searchFilters
  const isGlampingCategories = isGlamping(searchFilters.categoryIds || [])

  // Non-SEO if has dates
  if (searchFilters.dates?.arrive || searchFilters.dates?.depart) {
    return false
  }

  // Non-SEO if has non-default party params
  const hasNonDefaultPartyParams =
    searchFilters.party &&
    ((searchFilters.party.adults !== undefined &&
      searchFilters.party.adults !== 2) ||
      (searchFilters.party.childAges !== undefined &&
        searchFilters.party.childAges.length > 0))

  if (hasNonDefaultPartyParams) {
    return false
  }

  // Non-SEO if has more than 1 facet
  if (searchFilters.facets?.length && searchFilters.facets.length > 1) {
    return false
  }

  // Non-SEO if has multiple categories (unless glamping)
  if ((searchFilters.categoryIds?.length ?? 0) > 1 && !isGlampingCategories) {
    return false
  }

  return true
})

const baseLangPrefix = computed(() => {
  return langCode === 'en-gb' ? '/' : `/${langCode}/`
})

function getBaseUrl(useSearch: boolean) {
  if (!isSeo.value || useSearch) {
    return `${baseLangPrefix.value}search`
  }
  const categoryIds = searchStore.searchFilters.categoryIds
  if (categoryIds?.length === 1) {
    const rootSlug = CATEGORIES[categoryIds[0]]
    return `${baseLangPrefix.value}${rootSlug}`
  }
  if (isGlampingCategory.value) {
    return `${baseLangPrefix.value}glamping`
  }
  return `${baseLangPrefix.value}campsites`
}

const searchFilters = computed((): SearchFilters => {
  const filters = searchStore.searchFilters
  return {
    ...filters,
    hierarchyPath: filters.hierarchyPath ?? undefined,
  }
})

// Only show secondary breadcrumbs on faceted search pages
const shouldShowSecondaryBreadcrumbs = computed(() => {
  const facets = searchFilters.value.facets || []
  return facets.length > 0
})

const breadcrumbCountsSearchFilters = computed(() => {
  return {
    ...searchFilters.value,
    facets: [], // Remove facets for hierarchy counts
  }
})

const isDatedSearch = computed(() => {
  return Boolean(searchFilters.value?.dates?.arrive)
})

const { breadcrumbCounts } = useBreadcrumbCounts(
  breadcrumbCountsSearchFilters,
) as { breadcrumbCounts: Ref<BreadcrumbCounts> }

const { data: facetRules } = await useAsyncData(
  `secondary-breadcrumb-facet-rules-${JSON.stringify(searchFilters.value)}`,
  async () => {
    if (!searchFilters.value.facets?.length) {
      return []
    }
    const rules = await Promise.all(
      searchFilters.value.facets.map((facet) => getFacetRules(facet, langCode)),
    )
    return rules.flat()
  },
  {
    watch: [searchFilters],
    server: true,
    lazy: import.meta.client,
  },
)

const hierarchyCounts = computed(
  () => breadcrumbCounts.value?.bookableFacetHierarchy ?? {},
)

// Use the same hierarchy counts for faceted (no separate faceted counts needed for secondary)
const facetedHierarchyCounts = computed(
  () => breadcrumbCounts.value?.bookableFacetHierarchy ?? {},
)

const homeBreadcrumb = computed(() => {
  const homeLinkGenerator = new HomepageLinkGenerator()
  return homeLinkGenerator.getUrlData(searchFilters.value)
})

const breadcrumbsInternal = computed(() => {
  const crumbs = computeSecondaryBreadcrumbs({
    breadcrumbs: props.breadcrumbs,
    counts: props.counts,
    searchFilters: searchFilters.value,
    facetRules: facetRules.value ?? null,
    hierarchyCounts: hierarchyCounts.value,
    facetedHierarchyCounts: facetedHierarchyCounts.value,
    isGlampingCategory: isGlampingCategory.value,
    isDatedSearch: isDatedSearch.value,
    getBaseUrl,
  })

  // Make the last breadcrumb linkable since it's not the current page
  // (it links to the unfaceted version)
  if (crumbs.length > 0) {
    const lastCrumb = crumbs[crumbs.length - 1]
    if (lastCrumb) {
      lastCrumb.linkable = true
    }
  }

  return crumbs
})

const renderedUrls = ref<Map<string, string>>(new Map())

const updateRenderedUrl = (label: string, url: string) => {
  renderedUrls.value.set(label, url)
}

const head = computed(() => {
  if (props.showSchema && shouldShowSecondaryBreadcrumbs.value) {
    const allBreadcrumbs = [
      {
        label: 'Home',
        id: `https://${useRuntimeConfig().public.envUrl}${homeBreadcrumb.value.path}`,
      },
      ...breadcrumbsInternal.value
        .filter(
          (crumb) =>
            crumb.linkable &&
            renderedUrls.value.has(crumb.label) &&
            crumb.hierarchyPath,
        )
        .map((crumb) => ({
          label: crumb.label,
          id: `https://${useRuntimeConfig().public.envUrl}${renderedUrls.value.get(crumb.label)}`,
        })),
    ]

    // Add the last breadcrumb
    const lastBreadcrumb =
      breadcrumbsInternal.value[breadcrumbsInternal.value.length - 1]
    if (lastBreadcrumb && lastBreadcrumb.linkable) {
      let finalUrl: string

      if (renderedUrls.value.has(lastBreadcrumb.label)) {
        finalUrl = renderedUrls.value.get(lastBreadcrumb.label)!
      } else {
        // No facets in secondary breadcrumbs - just use the URL as-is
        finalUrl = lastBreadcrumb.url
      }

      allBreadcrumbs.push({
        label: lastBreadcrumb.label,
        id: `https://${useRuntimeConfig().public.envUrl}${finalUrl}`,
      })
    }

    return {
      script: [
        {
          hid: 'ldjson-schema-secondary-breadcrumbs',
          type: 'application/ld+json',
          body: true,
          json: {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: allBreadcrumbs.map((breadcrumb, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@id': breadcrumb.id,
                name: breadcrumb.label,
              },
            })),
          },
        },
      ],
    }
  }
  return {}
})
</script>

<style>
@import url('~/assets/css/breadcrumbs.css');
</style>
